import styled, { css } from 'styled-components';

import { TransientProps } from '@common/types';

export const InfoCard = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[100]};
    border-radius: 1.25rem;
    padding: 1.5rem;
    border: 1px solid ${theme.colors.base.border};
    box-shadow: ${theme.shadows.sm};
    margin-bottom: 1rem;
    max-width: 1280px;
  `}
`;

export const TagsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
`;

export const Tag = styled.span`
  ${({ theme }) => css`
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.1875rem 0.625rem;
    border-radius: 9999px;
    background: ${theme.colors.brand.primary.opaque};
    color: ${theme.colors.brand.primary.default};
  `}
`;

export const Title = styled.h2`
  ${({ theme }) => css`
    font-size: 1.25rem;
    font-weight: 800;
    color: ${theme.colors.foreground[100]};
    line-height: 1.3;
    margin-bottom: 0.75rem;
  `}
`;

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.125rem;
`;

export const MetaItem = styled.span`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.3125rem;
    font-size: 0.8125rem;
    color: ${theme.colors.foreground[40]};

    svg {
      width: 0.9375rem;
      height: 0.9375rem;
      flex-shrink: 0;
    }
  `}
`;

export const Divider = styled.hr`
  ${({ theme }) => css`
    width: 100%;
    height: 1px;
    background: ${theme.colors.background[80]};
    border: none;
    margin-bottom: 1.125rem;
  `}
`;

export const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

export const AuthorLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const AuthorAvatar = styled.div`
  ${({ theme }) => css`
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 9999px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${theme.colors.brand.secondary.default};
    color: ${theme.colors.brand.secondary.text};
    font-weight: 700;
    font-size: 0.9375rem;
  `}
`;

export const AuthorName = styled.div`
  ${({ theme }) => css`
    font-size: 0.9375rem;
    font-weight: 700;
    color: ${theme.colors.foreground[100]};
  `}
`;

export const AuthorRole = styled.div`
  ${({ theme }) => css`
    font-size: 0.8125rem;
    color: ${theme.colors.foreground[40]};
  `}
`;

export const ActionsBar = styled.div`
  display: flex;
  gap: 0.5rem;
`;

type ActionPillProps = TransientProps<{ liked?: boolean }>;

export const ActionPill = styled.button<ActionPillProps>`
  ${({ theme, $liked }) => css`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 1rem;
    border-radius: 9999px;
    border: 1.5px solid
      ${$liked ? theme.colors.error[100] : theme.colors.base.border};
    background: ${$liked
      ? theme.colors.error[40]
      : theme.colors.background[100]};
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${$liked ? theme.colors.error[100] : theme.colors.foreground[60]};
    cursor: pointer;
    transition: ${theme.transition.default};

    svg {
      width: 1rem;
      height: 1rem;
      flex-shrink: 0;
      ${$liked &&
      css`
        fill: ${theme.colors.error[100]};
      `}
    }

    :hover {
      border-color: ${$liked
        ? theme.colors.error[100]
        : theme.colors.brand.primary.default};
      color: ${$liked
        ? theme.colors.error[100]
        : theme.colors.brand.primary.default};
      background: ${$liked
        ? theme.colors.error[40]
        : theme.colors.brand.primary.opaque};
    }
  `}
`;

export const DescriptionBlock = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[80]};
    border-radius: 1rem;
    padding: 1rem 1.25rem;
    margin-bottom: 1rem;
    max-width: 1280px;
    cursor: pointer;
    transition: background ${theme.transition.default};

    :hover {
      background: ${theme.colors.background[60]};
    }
  `}
`;

export const DescriptionText = styled.p<{ $expanded: boolean }>`
  ${({ theme, $expanded }) => css`
    font-size: 0.875rem;
    line-height: 1.7;
    color: ${theme.colors.foreground[80]};
    white-space: pre-wrap;

    ${!$expanded &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
  `}
`;

export const ToggleButton = styled.span`
  ${({ theme }) => css`
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${theme.colors.brand.primary.default};
    margin-top: 0.5rem;
    display: inline-block;
  `}
`;
